import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { CataloguePageHeader } from '@/components/catalogue/Header';
import BundleOverviewCard from '@/components/catalogue/bundle/BundleOverviewCard';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { TextInput } from '@/components/form/fields/text/TextInput';
import { UserGuideButton } from '@/components/guide/UserGuideButton';
import { Card } from '@/components/sections/Card';
import SiChevronLeft from '@/icons/SiChevronLeft';
import SiSearch from '@/icons/SiSearch';
import { useBundle } from '@/services/bundles/useBundleService';
import { useNavigateAndValidate } from '@/services/catalogue/validation/catalogueHooks';
import { useCustomerRoutePrefix } from '@/services/customer/useCustomerRoutePrefix';
import { useSearch } from '@/utils/search';
import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router';

interface CustomerBundlesProps {}

const CustomerBundles: FunctionComponent<CustomerBundlesProps> = () => {
  const routePrefix = useCustomerRoutePrefix();
  const navigateAndValidate = useNavigateAndValidate();
  const navigate = useNavigate();

  const breadcrumbs: NavPage[] = [
    {
      name: 'Bundles',
      to: ''
    }
  ];

  const bundles = useBundle(state => state.bundles);

  const financeModelUrl = `${routePrefix}/financeModels`;

  const [searchQuery, setSearchQuery] = useState<string>('');
  const filteredBundlesByName = useSearch(bundles, ['name'], searchQuery);

  return (
    <div className="flex h-full flex-1 flex-col bg-white bg-opacity-50">
      <CataloguePageHeader title={'My Bundles'} breadcrumbs={breadcrumbs}>
        <div className="flex gap-4">
          <UserGuideButton newRoute={`${routePrefix}/bundles/guide`} />
          <Button
            onClick={() => {
              navigateAndValidate(financeModelUrl);
            }}
            variant={ButtonVariant.Mono}
          >
            <SiChevronLeft className="text-xl" />
            <span>Back to Finance Models</span>
          </Button>
          <Button
            onClick={() => {
              navigate(`create`);
            }}
            variant={ButtonVariant.Secondary}
          >
            Create New Bundle
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            onClick={() => {
              navigateAndValidate(`${routePrefix}/purchaseOrders`);
            }}
          >
            Orders / Quotes
          </Button>
        </div>
      </CataloguePageHeader>
      <div className="flex-1 overflow-y-auto px-6 py-8 space-y-3">
        <TextInput
          id="filter"
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={'Search bundles'}
          prefix={<SiSearch className="text-xl" />}
        />
        {bundles.length > 0 ? (
          <>
            {filteredBundlesByName.map(bundle => (
              <BundleOverviewCard key={bundle.id} bundle={bundle} />
            ))}
          </>
        ) : (
          <Card className="py-5 px-10 flex flex-col items-center gap-5 w-2/3 mx-auto max-w-6xl">
            <div className="w-full font-medium space-y-5">
              <p>Bundles are collections of products intended for specific groups of people.</p>
              <div>
                <p>For example, you might want:</p>
                <ul className="pl-7 list-disc">
                  <li>A 'marketing' bundle with a lightweight laptop and a headset.</li>
                  <li>A 'developers' bundle with a desktop and monitors.</li>
                  <li>An 'off site staff' bundle with a phone, case and spare battery.</li>
                </ul>
              </div>
              <p>Get started by creating your first bundle!</p>
            </div>
            <Button
              onClick={() => {
                navigate(`create`);
              }}
              variant={ButtonVariant.Secondary}
            >
              Create New Bundle
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CustomerBundles;
